import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { ListWorkflows, CreateWorkflow } from '../../Client/ManagementServiceClient';
import './styles.css';

// Turn a hydrated workflow into the row's word-status and pill. Projects resume
// where they left off, described in words rather than a step number.
const deriveStatus = (item) => {
    const workflow = item.workflow || {};
    const workspace = item.workspace || {};
    const post = item.post || {};
    const step = String(workflow.CurrentStep || 'idea');

    const posted = Boolean(workspace.IsPosted) || String(post.State || '').toLowerCase() === 'posted';
    if (posted) {
        return { pill: 'live', pillLabel: 'Live', at: 'Posted', action: 'Report' };
    }
    if (step === 'post' || step === 'analysis') {
        const version = Number(workspace.Version) || 0;
        return { pill: 'ready', pillLabel: 'Ready', at: version ? `v${version} ready` : 'Ready to post', action: 'Resume' };
    }
    if (step === 'workspace') {
        const version = Number(workspace.Version) || 0;
        return { pill: 'draft', pillLabel: 'Draft', at: `at Edit · draft v${version}`, action: 'Resume' };
    }
    return { pill: 'draft', pillLabel: 'Draft', at: 'at Idea', action: 'Resume' };
};

// The workflow carries the only name in the project.
const projectName = (item) =>
    item.workflow?.Name || item.workflow?.WorkflowId || 'Untitled project';

function Projects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(null); // 'quick-edit' | 'full' | null
    const [newProjectName, setNewProjectName] = useState('');

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await ListWorkflows(idToken, null, 10);
            if (status === 200 && myJson.workflows) {
                setProjects(myJson.workflows);
            } else {
                setError('Failed to load projects');
            }
        } catch (err) {
            setError(err.message || 'Error loading projects');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const startProject = async (flowType) => {
        try {
            setCreating(flowType);
            setError(null);
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            // Use the name the user typed; fall back to a sensible default per
            // flow so an unnamed project still creates. Users can rename it
            // later in the Idea step.
            const fallbackName = flowType === 'quick-edit' ? 'New edit' : 'New idea';
            const name = newProjectName.trim() || fallbackName;
            const { status, myJson } = await CreateWorkflow(idToken, {
                name,
                text: '',
                prompt: '',
                flow_type: flowType,
                collaborators: [],
            });
            if (status === 200 && myJson.workflow) {
                navigate(`/Workflow/${myJson.workflow.WorkflowId}`);
            } else {
                setError('Failed to create project');
            }
        } catch (err) {
            setError(err.message || 'Error creating project');
        } finally {
            setCreating(null);
        }
    };

    return (
        <div className="projects-page">
            <header className="projects-hero">
                <h1>Start a project</h1>
                <p>Every video is a project that moves Idea → Edit → Post → Report. Pick where you want to begin.</p>
                <label className="projects-name-field">
                    <span>Project name <em>(optional — you can rename it later)</em></span>
                    <input
                        type="text"
                        className="projects-name-input"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder="e.g. Spring launch teaser"
                        maxLength={120}
                        disabled={creating !== null}
                    />
                </label>
            </header>

            <div className="intent-cards">
                <div className="intent-card primary">
                    <h2>Start from an idea</h2>
                    <p>Write the concept first, get AI feedback on the script, then move into the edit.</p>
                    <div className="intent-path">Path: <b>Idea → Edit → Post → Report</b></div>
                    <button
                        type="button"
                        className="intent-btn accent"
                        onClick={() => startProject('full')}
                        disabled={creating !== null}
                    >
                        {creating === 'full' ? 'Starting…' : 'New idea'}
                    </button>
                </div>
                <div className="intent-card">
                    <h2>Edit a video</h2>
                    <p>Jump straight into the editor with your own footage. No ideation, no setup.</p>
                    <div className="intent-path">Path: <b>Edit → Post</b> · Report unlocks after posting</div>
                    <button
                        type="button"
                        className="intent-btn"
                        onClick={() => startProject('quick-edit')}
                        disabled={creating !== null}
                    >
                        {creating === 'quick-edit' ? 'Starting…' : 'Start editing'}
                    </button>
                </div>
            </div>

            {error && <div className="projects-error">{error}</div>}

            <section className="projects-continue">
                <h3>Continue</h3>
                {loading ? (
                    <p className="projects-empty">Loading projects…</p>
                ) : projects.length === 0 ? (
                    <p className="projects-empty">No projects yet — start one above.</p>
                ) : (
                    <ul className="project-list">
                        {projects.map((item) => {
                            const workflowId = item.workflow?.WorkflowId;
                            const status = deriveStatus(item);
                            return (
                                <li key={workflowId}>
                                    {/* A link, so the row opens from the keyboard, in a new
                                        tab, and reads as a link to a screen reader. */}
                                    <Link className="project-row" to={`/Workflow/${workflowId}`}>
                                        <span className="project-row-name">{projectName(item)}</span>
                                        <span className="project-row-at">{status.at}</span>
                                        <span className={`project-pill pill-${status.pill}`}>{status.pillLabel}</span>
                                        <span className="project-row-go" aria-hidden="true">{status.action} <ArrowRight size={14} /></span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
}

export { Projects };
